import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { useWizardSync } from '../lib/store/useWizardSync';
import { useWizardStore } from '../lib/store/wizard';
import { getStep } from '../components/hub/dimensions';
import {
  REVIEW_DIMENSIONS,
  ConnectedBeraterDimensionReview,
} from '../components/cockpit/BeraterDimensionReviewSection';

/**
 * Deep-Link-Route für die fachliche Bearbeitung EINER Dimension (Berater).
 * Pendant zur gestapelten Cockpit-Sektion, aber fokussiert + direkt verlinkbar
 * (z.B. aus dem „Einzelansicht"-Link im Cockpit-Kartenkopf).
 *
 * Mountet denselben Zwei-Ebenen-Sync wie das Cockpit (`layer: 'berater'`), damit
 * beide Ebenen geladen werden. Nur die Freitext-Dimensionen sind bearbeitbar;
 * unbekannte/strukturelle `:dim`-Werte (z.B. resources) zeigen einen Hinweis.
 */
export function BeraterDimensionRoute() {
  const { id: caseId = 'demo', dim = '' } = useParams<{ id: string; dim: string }>();
  const navigate = useNavigate();
  const auth = useAuthOrSession();
  const { isMock } = auth;

  useWizardSync(caseId, {
    userId: isMock ? null : (auth.user?.id ?? null),
    layer: 'berater',
  });

  const caseExists = useWizardStore((s) => Boolean(s.cases[caseId]));
  const ensureCase = useWizardStore((s) => s.ensureCase);
  useEffect(() => {
    if (!caseExists) ensureCase(caseId);
  }, [caseExists, ensureCase, caseId]);

  const def = REVIEW_DIMENSIONS.find((d) => d.key === dim);

  return (
    <div className="pb-24 pt-12 md:pt-20">
      <button
        type="button"
        onClick={() => {
          void navigate(`/case/${caseId}/cockpit`);
        }}
        className="mb-6 inline-flex items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft size={14} />
        {de.beraterReview.backToCockpit}
      </button>

      {def ? (
        <>
          <header className="relative pb-10">
            <div
              aria-hidden="true"
              className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
              style={{ backgroundColor: getStep(def.key).accentHex, opacity: 0.32 }}
            />
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              {de.beraterReview.singleKicker}
            </p>
            <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
              {de.dimensions[def.key].name}
            </h1>
            <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:text-lg">
              {de.beraterReview.singleSubtitle}
            </p>
          </header>
          <ConnectedBeraterDimensionReview caseId={caseId} dimension={def.key} />
        </>
      ) : (
        <p className="font-serif text-base italic text-stone-500">
          {de.beraterReview.dimensionNotFound}
        </p>
      )}
    </div>
  );
}
